<template>
  <div class="bg-white rounded border border-gray-200">
    <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h2 class="text-sm font-semibold text-gray-900">Data Files</h2>
        <p class="text-xs text-gray-500">Your data delivery area — the same folders your SFTP drops land in</p>
      </div>
      <label class="inline-flex items-center px-3 py-1.5 rounded bg-primary-500 text-white text-sm cursor-pointer hover:bg-primary-600">
        <FontAwesomeIcon :icon="['fas', 'paperclip']" class="mr-1.5 text-xs" />
        Upload here
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          multiple
          :accept="acceptString"
          @change="onFileSelect"
        />
      </label>
    </div>

    <div v-if="loading" class="p-6">
      <LoadingIndicator message="Loading..." />
    </div>

    <div v-else-if="loadError" class="p-6 text-sm text-gray-600">
      Couldn't load your files.
      <button class="text-primary-500 hover:underline ml-1" @click="load(pathSegments)">Retry</button>
    </div>

    <div
      v-else
      :class="dragOver ? 'bg-primary-50/50' : ''"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop.prevent="onDrop"
    >
      <!-- Breadcrumb -->
      <div class="px-4 py-2 border-b border-gray-100 flex items-center flex-wrap gap-1 text-sm">
        <button
          class="text-gray-500 hover:text-primary-500"
          :class="{ 'font-medium text-gray-900': !pathSegments.length }"
          @click="navigate([])"
        >
          <FontAwesomeIcon :icon="['fas', 'folder']" class="mr-1 text-xs opacity-60" />Home
        </button>
        <template v-for="(seg, i) in pathSegments" :key="i">
          <span class="text-gray-300">/</span>
          <button
            class="text-gray-500 hover:text-primary-500"
            :class="{ 'font-medium text-gray-900': i === pathSegments.length - 1 }"
            @click="navigate(pathSegments.slice(0, i + 1))"
          >
            {{ seg }}
          </button>
        </template>
      </div>

      <!-- In-flight uploads -->
      <div v-if="stagedFiles.length" class="px-4 pt-3 space-y-1.5">
        <div
          v-for="(file, index) in stagedFiles"
          :key="index"
          class="flex items-center justify-between bg-gray-50 rounded px-3 py-1.5"
        >
          <div class="flex items-center gap-2 min-w-0">
            <FontAwesomeIcon :icon="['fas', 'paperclip']" class="text-gray-400 text-xs shrink-0" />
            <span class="text-sm text-gray-700 truncate">{{ file.name }}</span>
            <span class="text-xs text-gray-400 shrink-0">{{ formatSize(file.size) }}</span>
            <span class="text-xs text-gray-400 shrink-0">→ {{ file._path || 'Home' }}</span>
          </div>
          <div class="flex items-center gap-2 shrink-0 ml-2">
            <div v-if="file._uploading" class="flex items-center gap-1.5 text-xs text-gray-400">
              <div class="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  class="h-full bg-primary-500 rounded-full transition-all"
                  :style="{ width: (file._progress || 0) + '%' }"
                ></div>
              </div>
              <span>{{ file._progress || 0 }}%</span>
            </div>
            <FontAwesomeIcon
              v-else-if="file._uploaded"
              :icon="['fas', 'check']"
              class="text-green-500 text-xs"
            />
            <span v-if="file._error" class="text-xs text-red-500">{{ file._errorMessage || 'Failed' }}</span>
            <button class="text-gray-400 hover:text-red-500" @click="removeStaged(index)">
              <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
            </button>
          </div>
        </div>
      </div>
      <p v-if="validationError" class="px-4 pt-2 text-xs text-red-500">{{ validationError }}</p>

      <!-- Listing: folders first, then files -->
      <div class="px-4 py-2">
        <p v-if="truncated" class="text-xs text-gray-400 py-1">
          Showing the {{ files.length }} most recent files.
        </p>
        <table v-if="folders.length || files.length" class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs text-gray-500 border-b border-gray-100">
              <th class="py-2 font-medium">Name</th>
              <th class="py-2 font-medium w-24">Size</th>
              <th class="py-2 font-medium w-44">Modified</th>
              <th class="py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="folder in folders"
              :key="'d:' + folder"
              class="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
              @click="navigate([...pathSegments, folder])"
            >
              <td class="py-2 text-gray-800" colspan="3">
                <FontAwesomeIcon :icon="['fas', 'folder']" class="text-amber-400 mr-2" />{{ folder }}
              </td>
              <td class="py-2 text-right text-gray-300">
                <FontAwesomeIcon :icon="['fas', 'chevron-right']" class="text-xs" />
              </td>
            </tr>
            <tr
              v-for="file in files"
              :key="file.key"
              class="border-b border-gray-50 last:border-0 hover:bg-gray-50"
            >
              <td class="py-2 text-gray-800 break-all pr-4">
                <FontAwesomeIcon :icon="['fas', 'file']" class="text-gray-300 mr-2" />{{ file.filename }}
              </td>
              <td class="py-2 text-gray-500 whitespace-nowrap pr-4">{{ formatSize(file.size) }}</td>
              <td class="py-2 text-gray-500 whitespace-nowrap pr-4">{{ formatDate(file.last_modified) }}</td>
              <td class="py-2 text-right whitespace-nowrap">
                <button
                  v-if="canImport"
                  class="text-xs text-gray-400 hover:text-primary-500 mr-3"
                  title="Import this file into app records"
                  @click="importFile = file"
                >
                  Import
                </button>
                <button
                  class="text-gray-400 hover:text-primary-500"
                  title="Download"
                  @click="downloadFile(file)"
                >
                  <FontAwesomeIcon :icon="['fas', 'download']" class="text-xs" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-sm text-gray-400 py-6 text-center">
          This folder is empty — drop files anywhere to upload them here.
        </p>
      </div>
    </div>

    <ModelImportModal
      v-if="importFile"
      :initial-file="{ s3_key: importFile.key, filename: importFile.filename }"
      @close="importFile = null"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import ModelImportModal from '@/components/ETL_Utility/ModelImportModal.vue';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { dataUploadsLocation } from '../../../router/data_routes';

// Clean instance for the S3 PUT: the app's default axios instance appends
// ?tenant_slug=... and CSRF headers via its request interceptor, which mutates
// a presigned URL and breaks the signature. A fresh instance has neither, and
// still gives us onUploadProgress (fetch can't report upload progress).
const s3axios = axios.create({ withCredentials: false });

export default {
  name: 'Customer_File_Upload',
  components: { FontAwesomeIcon, LoadingIndicator, ModelImportModal },
  data() {
    return {
      pathSegments: [],
      folders: [],
      files: [],
      truncated: false,
      allowedExtensions: [],
      maxBytes: 5 * 1024 * 1024 * 1024,
      stagedFiles: [],
      dragOver: false,
      loading: true,
      loadError: false,
      validationError: '',
      importFile: null,
    };
  },
  computed: {
    // Model Import is superuser/engineer machinery — customers with only the
    // upload feature never see the button (and the API would 403 them anyway).
    canImport() {
      return canAccessFeature(FEATURES.MODULE_DATA_MODEL_IMPORT);
    },
    currentPath() {
      return this.pathSegments.join('/');
    },
    acceptString() {
      return this.allowedExtensions.join(',');
    },
    // The open folder lives in the path on /data: /data/uploads/a/b. Null when
    // this pane isn't the current route (hidden tab, or used outside /data),
    // so nothing reloads or resets while it is out of view.
    routeSegments() {
      const route = this.$route;
      if (!route) return null;
      if (route.name === 'DataUploads') {
        const path = route.params.path;
        return (Array.isArray(path) ? path : [path]).filter(Boolean);
      }
      if (route.name === 'DataTab' && route.params.tab === 'uploads') return [];
      return null;
    },
    routeKey() {
      return this.routeSegments ? this.routeSegments.join('/') : null;
    },
  },
  watch: {
    routeKey(key, prev) {
      if (key == null) return;
      // Coming back to the bare tab URL from another tab keeps the open folder.
      if (prev == null && key === '' && this.pathSegments.length) {
        this.$router.replace(dataUploadsLocation(this.pathSegments));
        return;
      }
      if (key !== this.currentPath) this.load(this.routeSegments);
    },
  },
  mounted() {
    this.load(this.routeSegments || []);
  },
  methods: {
    // Folder/breadcrumb clicks: a history step on /data, in-memory elsewhere.
    navigate(segments) {
      if (this.routeSegments) this.$router.push(dataUploadsLocation(segments));
      else this.load(segments);
    },
    async load(segments) {
      this.pathSegments = [...segments];
      this.loading = true;
      this.loadError = false;
      this.validationError = '';
      try {
        const { data } = await axios.get('/api/etl_module/customer-uploads/', {
          params: { path: this.currentPath },
        });
        this.folders = data.folders;
        this.files = data.files;
        this.truncated = data.truncated;
        this.allowedExtensions = data.allowed_extensions;
        this.maxBytes = data.max_bytes;
      } catch {
        this.loadError = true;
      }
      this.loading = false;
    },
    async refresh() {
      try {
        const { data } = await axios.get('/api/etl_module/customer-uploads/', {
          params: { path: this.currentPath },
        });
        this.folders = data.folders;
        this.files = data.files;
        this.truncated = data.truncated;
      } catch {
        // keep the current listing; the staged row already shows the outcome
      }
    },
    onFileSelect(event) {
      this.addFiles(Array.from(event.target.files));
      this.$refs.fileInput.value = '';
    },
    onDrop(event) {
      this.dragOver = false;
      this.addFiles(Array.from(event.dataTransfer.files));
    },
    addFiles(files) {
      this.validationError = '';
      for (const file of files) {
        if (file.size > this.maxBytes) {
          this.validationError = `"${file.name}" is too large. Max ${this.formatSize(this.maxBytes)}.`;
          continue;
        }
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!this.allowedExtensions.includes(ext)) {
          this.validationError = `"${file.name}" file type not allowed.`;
          continue;
        }
        const entry = Object.assign(file, {
          _uploading: false,
          _uploaded: false,
          _error: false,
          _errorMessage: '',
          _path: this.currentPath,
        });
        this.stagedFiles.push(entry);
        this.uploadFile(entry);
      }
    },
    async uploadFile(file) {
      file._uploading = true;
      file._progress = 0;
      this.stagedFiles = [...this.stagedFiles];
      try {
        const { data: presigned } = await axios.post('/api/etl_module/customer-uploads/presign/', {
          path: file._path,
          filename: file.name,
          size: file.size,
        });
        await s3axios.put(presigned.upload_url, file, {
          onUploadProgress: (e) => {
            if (e.total) {
              file._progress = Math.round((e.loaded / e.total) * 100);
              this.stagedFiles = [...this.stagedFiles];
            }
          },
        });
        file._uploading = false;
        file._uploaded = true;
        if (file._path === this.currentPath) {
          await this.refresh();
        }
      } catch (err) {
        file._uploading = false;
        file._error = true;
        file._errorMessage = err?.response?.data?.error || 'Failed';
      }
      this.stagedFiles = [...this.stagedFiles];
    },
    removeStaged(index) {
      this.stagedFiles.splice(index, 1);
    },
    async downloadFile(file) {
      try {
        const { data } = await axios.get('/api/etl_module/customer-uploads/download/', {
          params: { key: file.key },
        });
        window.open(data.presigned_url, '_blank', 'noopener');
      } catch {
        this.validationError = `Couldn't download "${file.filename}".`;
      }
    },
    formatSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
      if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    },
    formatDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    },
  },
};
</script>
